import {
  Modal,
  ProgressIndicator,
  ProgressStep,
  InlineLoading,
} from 'carbon-components-react';
import { useEffect, useState } from 'react';
import Step1 from '../Steps/Step1';
import Step2 from '../Steps/Step2';
import Step3 from '../Steps/Step3';
import Step4 from '../Steps/Step4';
import Step5 from '../Steps/Step5';
import {
  defaultValues,
  LOCALE,
  segmentsTrack,
  selectOptions,
  SKELETON_IMAGE,
} from '../../../constants/default';
import {
  imageToBase64,
  prepareProductsUrl,
  SOLUTION_HEADER,
} from '../../../lib/helpers';
import { AssessmentsProps } from '../../../lib/interfaces';
import { useSmallBreakpoint } from 'src/common/hooks/widgets/useBreakpoint';

const Assessments = ({
  open,
  setPDFObj,
  localeCode,
  useTranslation,
  updateLocaleCode,
  handleCloseModal,
  handleDownloadPDF,
  isActiveStep,
}: AssessmentsProps) => {
  interface PostData {
    [key: string]: any;
  }
  const pdfPortfolio = 'images/pdfPortfolio.png';
  const isSmall = useSmallBreakpoint('560px');

  let steps = defaultValues('steps', useTranslation) || [];
  if (isActiveStep)
    steps = defaultValues('steps_secondary', useTranslation) || [];
  const solutionArray: any[] =
    defaultValues('solutionData', useTranslation) || [];
  const advancedItems: any =
    defaultValues('advancedItems', useTranslation) || [];
  const businessContinuityItems: any =
    defaultValues('businessContinuity', useTranslation) || [];
  const connectivityItems: any =
    defaultValues('connectivityItems', useTranslation) || [];
  const cyberResilienceItems: any =
    defaultValues('cyberResilience', useTranslation) || [];
  const _isLastStep = isActiveStep ? 4 : 5;

  const [step, setStep] = useState(1);
  const [isRequired, setIsRequired] = useState<boolean>(true);
  const [productObj, setProductObj] = useState({});
  const [postObj, setPostObj] = useState<PostData>({});
  const [jsonObj, setJSONObj] = useState<PostData>({});
  const [pdfImage3String, setBase64Image3] = useState('');
  const [productUrl, setProductUrl] = useState<string>();
  const [pdfPortfolioString, setBase64PrtImage] = useState(SKELETON_IMAGE);
  const [loadInline, setLoadInline] = useState(false);
  const [solutionData, setSoltionData] = useState(solutionArray);

  if (pdfPortfolio) {
    imageToBase64(pdfPortfolio).then((base64String) => {
      setBase64PrtImage(base64String);
    });
  }

  const initOCConfig = async () => {
    await fetch(prepareProductsUrl(), {
      method: 'GET',
      headers: SOLUTION_HEADER,
    })
      .then((res) => res.json())
      .then(async (data) => {
        if (!!Object.keys(data).length) {
          if (!data.hasOwnProperty(localeCode)) {
            await updateLocaleCode?.(LOCALE);
            localeCode = LOCALE;
          }
          const pobj = await data[localeCode];
          setJSONObj(pobj);
        }
      })
      .catch((error) => {
        console.log('error while fetching omnichannel products', error);
      });
  };

  useEffect(() => {
    initOCConfig();
  }, []);

  useEffect(() => {
    if (pdfImage3String) {
      let newSolutionData = solutionData;
      if (isActiveStep && !!solutionData?.length) {
        const idsToRemove = [1, 2, 3, 4];
        newSolutionData = solutionData.filter(
          (obj, index) => !idsToRemove.includes(index),
        );
      }
      const props = {
        productObj: productObj,
        pdfImage3String,
        pdfPortfolioString,
        useTranslation,
        solutionData: newSolutionData,
        localeCode,
        isActiveStep,
      };
      setPDFObj(props);
    }
    if (loadInline && pdfImage3String) {
      setLoadInline(false);
      setIsRequired(false);
      handleDownloadPDF();
    }
  }, [pdfImage3String, loadInline]);

  const generateLogic = (obj: any) => {
    const selectedFeatures = Object.keys(obj).filter(
      (key) => obj[key] === true,
    );
    const matchingCategories = selectOptions.filter((cat) =>
      selectedFeatures.some((feature) => cat.features.includes(feature)),
    );
    const resultArray = matchingCategories.map((category) => category.name);
    return resultArray;
  };

  const onGenerateLogic = async () => {
    setProductObj({});
    setIsRequired(true);
    let modelArr = generateLogic(postObj);
    const {
      flashTechnology,
      capacity,
      capacityOpt,
      virtualMachine,
      physicalHosts,
      localCopies,
      localVCopies,
    } = postObj;
    const capacityId = capacityOpt?.id || capacity?.id;
    const virtualMachineId = virtualMachine?.id;
    const physicalHostsId = physicalHosts?.id;
    const localCopiesId = localCopies?.id;
    const localVCopiesId = localVCopies?.id;
    const capacityText = useTranslation('pdf_page3_capacity_1', false, {
      capacity: capacityOpt?.text || capacity?.text,
    }) as string;

    if (capacityText) {
      solutionData[0]['value'] = [
        capacityText,
        useTranslation('pdf_page3_capacity_2', true) as string,
        useTranslation('pdf_page3_capacity_3', true) as string,
      ];
    }
    if (physicalHostsId)
      solutionData[1]['value'] = useTranslation('pdf_page3_physical', false, {
        hosts: physicalHosts?.text,
      }) as string;
    if (virtualMachineId)
      solutionData[2]['value'] = useTranslation('pdf_page3_approx', false, {
        approx: virtualMachine?.text,
      }) as string;
    if (localCopiesId)
      solutionData[3]['value'] = useTranslation('pdf_page3_approx', false, {
        approx: localCopies?.text,
      }) as string;
    if (localVCopiesId)
      solutionData[4]['value'] = useTranslation('pdf_page3_approx', false, {
        approx: localVCopies?.text,
      }) as string;

    const selectedConnectivity = connectivityItems
      .filter((item: any) => postObj[item.value])
      .map((item: any) => item.text);
    solutionData[5]['value'] = !!selectedConnectivity.length
      ? selectedConnectivity
      : solutionData[5]['value'];

    const selectedAdvanced = advancedItems
      .filter((item: any) => postObj[item.value])
      .map((item: any) => item.text);
    solutionData[6]['value'] = !!selectedAdvanced.length
      ? selectedAdvanced
      : solutionData[6]['value'];

    const selectedBusiness = businessContinuityItems
      .filter((item: any) => postObj[item.value])
      .map((item: any) => item.text);
    solutionData[7]['value'] = !!selectedBusiness.length
      ? selectedBusiness
      : solutionData[7]['value'];

    const selectedCyber = cyberResilienceItems
      .filter((item: any) => postObj[item.value])
      .map((item: any) => item.text);
    solutionData[8]['value'] = !!selectedCyber.length
      ? selectedCyber
      : solutionData[8]['value'];

    if (
      [
        physicalHostsId,
        virtualMachineId,
        localCopiesId,
        localVCopiesId,
      ].includes(3)
    )
      modelArr.push('C');
    if (
      [4, 5].includes(physicalHostsId) ||
      [4, 5].includes(virtualMachineId) ||
      [4, 5].includes(localCopiesId) ||
      [4, 5].includes(localVCopiesId)
    )
      modelArr.push('D');

    let techCategory = ['A', 'B', 'C', 'D', 'E'];
    if (!modelArr.length) modelArr = techCategory;
    if (flashTechnology) techCategory = ['A'];
    const filteredEntries = Object.entries(jsonObj).filter(([key]) => {
      return (
        key[0] === String(capacityId) &&
        modelArr.includes(key[1]) &&
        techCategory.includes(key[2])
      );
    });

    setSoltionData(solutionData);
    if (!!filteredEntries.length) {
      const sortedEntries = filteredEntries.sort(([keyA], [keyB]) => {
        return Number(keyA[3]) - Number(keyB[3]);
      });
      const firstMatch = sortedEntries[0];
      const pValue = await firstMatch[1];
      const pUrl = pValue.replace(
        /^prod-/,
        `https://www.ibm.com/flashsystem/pricing/api/products/${localeCode}/`,
      );
      const postsResponse = await fetch(pUrl);
      if (!postsResponse.ok) throw new Error('Failed to fetch posts');
      const postsData = await postsResponse.json();
      if (!!Object.keys(postsData).length) {
        const imgPath = `https://www.ibm.com/flashsystem/pricing/static/product-images/${postsData.family}/ProductLeadspace_sm_1.png`;
        setProductUrl(imgPath);
        setProductObj(postsData);
      }
      setIsRequired(false);
    }
  };

  const onDownloadPdf = () => {
    setLoadInline(true);
    setIsRequired(true);
  };

  const primaryButtonText = () => {
    switch (step) {
      case _isLastStep:
        return loadInline && !pdfImage3String ? (
          <InlineLoading
            status="active"
            description={useTranslation('btn_download_pdf')}
          />
        ) : (
          useTranslation('btn_download_pdf')
        );
      default:
        return useTranslation('btn_next');
    }
  };

  return (
    <div className="bx--grid">
      <Modal
        id="assessment-modal"
        open={open}
        modalHeading={useTranslation('modal_header')}
        onRequestClose={handleCloseModal}
        primaryButtonText={primaryButtonText()}
        secondaryButtonText={useTranslation(
          step > 1 ? 'btn_back' : 'btn_close',
        )}
        preventCloseOnClickOutside={true}
        primaryButtonDisabled={isRequired}
        size="lg"
        onRequestSubmit={() => {
          if (step === 1 || step <= _isLastStep - 1) {
            setStep((step) => step + 1);
          }
          if (step === _isLastStep - 1) {
            onGenerateLogic();
          }
          if (step === _isLastStep) {
            onDownloadPdf();
          }
          segmentsTrack(
            step === _isLastStep
              ? 'Download Now button click'
              : 'Next button click',
            'Assessment Page',
          );
        }}
        onSecondarySubmit={() => {
          if (step === _isLastStep + 1) {
            setStep((step) => step - 1);
          } else {
            if (step > 1) {
              setStep((step) => step - 1);
              setIsRequired(false);
            } else {
              handleCloseModal();
              setStep(1);
            }
          }
          segmentsTrack(
            step > 1 ? 'Back button click' : 'Close button click',
            'Assessment Page',
          );
        }}
        className="bg-gray"
      >
        <div className="bx--row fixHeight">
          <div className="bx--col-lg-4 bx--col-md-2 bx--col-sm-16 bg-white">
            <ProgressIndicator
              spaceEqually={true}
              currentIndex={step - 1}
              vertical={!isSmall}
              className="progress-indicator"
            >
              {steps &&
                steps.map((stepObj: any, index) => (
                  <ProgressStep
                    key={index}
                    complete={stepObj.value <= step}
                    index={stepObj.value}
                    label={stepObj.label}
                  />
                ))}
            </ProgressIndicator>
          </div>
          <div className="bx--col-lg-12 bx--col-md-6 bx--col-sm-4 bg-gray">
            {step === 1 && (
              <Step1
                postObj={postObj}
                setPostObj={setPostObj}
                setIsRequired={setIsRequired}
                useTranslation={useTranslation}
              />
            )}
            {step === 2 && (
              <Step2
                postObj={postObj}
                setPostObj={setPostObj}
                useTranslation={useTranslation}
              />
            )}
            {step === 3 && (
              <Step3
                postObj={postObj}
                setPostObj={setPostObj}
                useTranslation={useTranslation}
              />
            )}
            {step === 4 && !isActiveStep && (
              <Step4
                postObj={postObj}
                setPostObj={setPostObj}
                useTranslation={useTranslation}
              />
            )}
            {((step === 4 && isActiveStep) ||
              (step === 5 && !isActiveStep)) && (
              <Step5
                loadInline={loadInline}
                productObj={productObj}
                productUrl={productUrl}
                useTranslation={useTranslation}
                setBase64Image3={setBase64Image3}
              />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Assessments;
